// Custom hook for handling user registration
import { useState, useEffect } from 'react';
import registrationService from '../services/registrationService';

export const useRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [entidades, setEntidades] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);
  const [municipiosCache, setMunicipiosCache] = useState(new Map()); // Cache para evitar consultas repetidas
  
  // Load entidades on hook initialization
  useEffect(() => {
    const loadEntidades = async () => {
      try {
        const entidadesData = await registrationService.getEntidadesFromAPI();
        setEntidades(entidadesData);
      } catch (error) {
        console.error('Error cargando entidades:', error);
        // Fallback to local data
        setEntidades(registrationService.getEntidades());
      }
    };
    
    loadEntidades();
  }, []);
  
  /**
   * Load municipalities for a specific entity
   * @param {string} entidadId - Entity ID
   * @param {boolean} forceRefresh - Force refresh bypassing cache
   */
  const loadMunicipios = async (entidadId, forceRefresh = false) => {
    if (!entidadId) {
      setMunicipios([]);
      return [];
    }
    
    // Verificar caché primero (solo si no se fuerza el refresh)
    if (!forceRefresh && municipiosCache.has(entidadId)) {
      const cachedMunicipios = municipiosCache.get(entidadId);
      setMunicipios(cachedMunicipios);
      return cachedMunicipios;
    }
    
    // Evitar consultas duplicadas si ya se está cargando
    if (loadingMunicipios) {
      return [];
    }
    
    setLoadingMunicipios(true);
    try {
      const municipiosData = await registrationService.getMunicipiosFromAPI(entidadId);
      
      // Guardar en caché (incluso si está vacío, para evitar consultas futuras)
      setMunicipiosCache(prev => new Map(prev.set(entidadId, municipiosData)));
      setMunicipios(municipiosData);
      
      return municipiosData;
    } catch (error) {
      console.error('Error cargando municipios:', error);
      // Guardar resultado vacío en caché para evitar reintentos
      setMunicipiosCache(prev => new Map(prev.set(entidadId, [])));
      setMunicipios([]);
      return [];
    } finally {
      setLoadingMunicipios(false);
    }
  };

  /**
   * Register a new user
   * @param {Object} formData - Complete form data
   * @returns {Promise}
   */
  const registerUser = async (formData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Validate user data
      const validation = registrationService.validateUserData(formData.user);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }
      
      // Prepare user data
      const userData = {
        curp: formData.user.curp || '',
        nombre: formData.user.nombre,
        apellidoPaterno: formData.user.apellidoPaterno,
        apellidoMaterno: formData.user.apellidoMaterno || '',
        sexo: formData.user.sexo || '1',
        fechaNacimiento: formData.user.fechaNacimiento,
        entidad: formData.user.entidad || '15',
        rol: formData.user.rol || '857',
        telefono: registrationService.formatPhone(formData.user.telefono),
        email: formData.user.email,
        password: formData.user.password
      };
      
      // Prepare institution data if provided
      let institutionData = null;
      if (formData.institution) {
        institutionData = {
          nombre: formData.institution.nombre,
          direccion: formData.institution.direccion,
          telefono: formData.institution.telefono,
          email: formData.institution.email,
          tipo: formData.institution.tipo,
          rfc: formData.institution.rfc
        };
      }
      
      // Prepare payment data if provided
      let paymentData = null;
      if (formData.payment) {
        paymentData = {
          planId: formData.payment.planId,
          metodoPago: formData.payment.metodoPago,
          tarjeta: formData.payment.tarjeta,
          facturacion: formData.payment.facturacion
        };
      }
      
      // Make the registration request
      const result = await registrationService.registerUser(
        userData, 
        institutionData, 
        paymentData
      );
      
      setSuccess(true);
      return result;
      
    } catch (err) {
      setError(err.message || 'Error en el registro');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };
  
  /**
   * Clear success state
   */
  const clearSuccess = () => {
    setSuccess(false);
  };
  
  /**
   * Reset all states
   */
  const reset = () => {
    setIsLoading(false);
    setError(null);
    setSuccess(false);
  };
  
  /**
   * Clear municipalities cache
   */
  const clearMunicipiosCache = () => {
    setMunicipiosCache(new Map());
    setMunicipios([]);
  };

  return {
    // States
    isLoading,
    error,
    success,
    entidades, // Dynamic entidades from API
    municipios, // Municipalities based on selected entity
    loadingMunicipios,
    
    // Actions
    registerUser,
    clearError,
    clearSuccess,
    reset,
    clearMunicipiosCache,
    loadMunicipios,
    
    // Utilities
    roles: registrationService.getRoles(),
    validateUserData: registrationService.validateUserData,
    formatPhone: registrationService.formatPhone,
    
    // API methods
    refreshEntidades: async () => {
      const entidadesData = await registrationService.getEntidadesFromAPI();
      setEntidades(entidadesData);
      return entidadesData;
    }
  };
};
